import React, { useCallback, useEffect, useState } from "react";
import { X, Clock } from "@phosphor-icons/react";
import api from "@/lib/api";

const fmtWhen = (s) => s ? new Date(s).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "";

const ACTION_STYLES = {
  creator_added: "bg-blue-50 text-blue-700 border-blue-200",
  creator_edited: "bg-slate-50 text-slate-700 border-slate-200",
  creator_deleted: "bg-rose-50 text-rose-700 border-rose-200",
  status_changed: "bg-indigo-50 text-indigo-700 border-indigo-200",
  script_status_changed: "bg-violet-50 text-violet-700 border-violet-200",
  payment_requested: "bg-amber-50 text-amber-700 border-amber-200",
  payment_released: "bg-emerald-50 text-emerald-700 border-emerald-200",
  payment_request_rejected: "bg-rose-50 text-rose-700 border-rose-200",
  cost_added: "bg-blue-50 text-blue-700 border-blue-200",
  cost_edited: "bg-slate-50 text-slate-700 border-slate-200",
  cost_deleted: "bg-rose-50 text-rose-700 border-rose-200",
  payment_logged: "bg-emerald-50 text-emerald-700 border-emerald-200",
  payment_deleted: "bg-rose-50 text-rose-700 border-rose-200",
};

export default function CampaignActivityDrawer({ open, onOpenChange, campaignId }) {
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const limit = 30;

  const load = useCallback(async (p) => {
    if (!campaignId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/campaigns/${campaignId}/activity`, { params: { page: p, limit } });
      setItems(data.items || []);
      setTotal(data.total || 0);
      setPage(p);
    } finally { setLoading(false); }
  }, [campaignId]);

  useEffect(() => { if (open) load(1); }, [open, load]);

  const totalPages = Math.max(1, Math.ceil(total / limit));

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex" data-testid="activity-drawer">
      <div className="flex-1 bg-black/40" onClick={() => onOpenChange(false)} />
      <div className="w-full sm:w-[460px] bg-white h-full shadow-2xl overflow-hidden flex flex-col">
        <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500">Audit trail</div>
            <div className="font-heading text-lg font-semibold text-slate-900">Campaign activity</div>
            <div className="text-xs text-slate-500 mt-0.5">{total} event{total === 1 ? "" : "s"}</div>
          </div>
          <button onClick={() => onOpenChange(false)} className="p-1.5 hover:bg-slate-100 rounded" data-testid="activity-close">
            <X size={16} />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="p-10 text-center text-sm text-slate-500">Loading…</div>
          ) : items.length === 0 ? (
            <div className="p-10 text-center">
              <Clock size={28} className="mx-auto text-slate-300 mb-2" weight="duotone" />
              <div className="text-sm text-slate-500">Nothing yet. Mutations will appear here as they happen.</div>
            </div>
          ) : (
            <ul className="divide-y divide-slate-100">
              {items.map((a) => (
                <li key={a.id} className="px-5 py-3" data-testid={`activity-${a.id}`}>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`text-[10px] rounded-full px-1.5 py-0.5 font-semibold border uppercase tracking-wider ${ACTION_STYLES[a.action] || "bg-slate-50 text-slate-600 border-slate-200"}`}>
                      {a.action.replace(/_/g, " ")}
                    </span>
                    <span className="text-[11px] text-slate-500">{fmtWhen(a.created_at)}</span>
                  </div>
                  <div className="text-sm text-slate-900 mt-1">{a.label}</div>
                  <div className="text-[11px] text-slate-500 mt-0.5">by {a.actor_name || "—"}</div>
                </li>
              ))}
            </ul>
          )}
        </div>
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-5 py-3 border-t border-slate-200 text-xs">
            <button disabled={page <= 1} onClick={() => load(page - 1)} className="text-slate-600 disabled:opacity-40" data-testid="activity-prev">← Prev</button>
            <span className="text-slate-500">Page {page} / {totalPages}</span>
            <button disabled={page >= totalPages} onClick={() => load(page + 1)} className="text-slate-600 disabled:opacity-40" data-testid="activity-next">Next →</button>
          </div>
        )}
      </div>
    </div>
  );
}
